import { AssigneeFilterComponent, AssigneeFilterValue, EMPTY_ASSIGNEE_FILTER } from '../../company/assignee-filter/assignee-filter';
import { CompanyContextService } from '../../../services/company-context';
import { DealAssigneeFilter } from '../../../services/api/deal';
import { Component, OnInit, OnDestroy, ChangeDetectorRef, inject } from '@angular/core';
import { CommonModule } from '@angular/common';
import { FormsModule } from '@angular/forms';
import { ActivatedRoute, Router, RouterModule } from '@angular/router';
import { CargoRequestService, CargoRequest, SearchCargoRequestsRequest } from '../../../services/api/cargo-request';
import { DealService, CargoDeal } from '../../../services/api/deal';
import { DictionaryService, CountryDto } from '../../../services/api/dictionary';
import { CustomsCalculationsService, CustomsCalculation, SearchCustomsCalculationsRequest } from '../../../services/api/customs-calculations';
import { LanguageService } from '../../../services/i18n/language';
import { AuthService } from '../../../services/auth/auth';
import { Subscription, forkJoin, of } from 'rxjs';
import { catchError } from 'rxjs/operators';
import { PaginationComponent } from '../../shared/pagination/pagination.component';
import { CalculationsListComponent } from '../../shared/customs-calculations/calculations-list/calculations-list';
import { PageResponse } from '../../../services/api/page-response';
import { ConfirmDialogService } from '../../../services/confirm-dialog';
import { DocNumberPipe } from '../../../services/request-number';
import { ResizableColumnsDirective } from '../../../services/resizable-columns.directive';
import { FfCabinetHeadComponent } from '../../freight-forwarder/cabinet-head/cabinet-head.component';

type CoTab = 'requests' | 'deals' | 'calculations' | 'supplies';

/** Заголовок страницы по разделу. */
const TAB_TITLES: Record<CoTab, { key: string; fallback: string }> = {
  requests: { key: 'coNav.freightRequests', fallback: 'Запросы на перевозку' },
  deals: { key: 'coNav.deals', fallback: 'Сделки' },
  calculations: { key: 'calcSections.myCalculations', fallback: 'Мои расчёты' },
  supplies: { key: 'coNav.supplies', fallback: 'Мои поставки' },
};

/** Маршрут, цена и текущий этап сделки — из карточки сделки (в списке их нет). */
interface DealInfo {
  route: string;
  /** Товары запроса — для строки поставки по сделке. */
  cargo: string;
  countries: string;
  requestCreatedAt: string;
  price: string;
  stage: string;
  stageDate: string | null;
}

@Component({
  selector: 'app-cargo-owner-dashboard',
  standalone: true,
  imports: [CommonModule, FormsModule, RouterModule, AssigneeFilterComponent, PaginationComponent, CalculationsListComponent, DocNumberPipe, FfCabinetHeadComponent, ResizableColumnsDirective],
  templateUrl: './cargo-owner-dashboard.html',
  styleUrl: './cargo-owner-dashboard.scss'
})
export class CargoOwnerDashboardComponent implements OnInit, OnDestroy {
  /** Подтверждение на странице вместо системного confirm() — его блокируют встроенные браузеры. */
  private confirmDialog = inject(ConfirmDialogService);

  // Tab management
  activeTab: 'requests' | 'deals' | 'calculations' | 'supplies' = 'requests';

  // Requests data
  requests: CargoRequest[] = [];
  loadingRequests = false;
  errorRequests: string | null = null;
  requestsPage: PageResponse<CargoRequest> | null = null;
  currentRequestsPage = 0;
  pageSize = 20;
  
  // Filters
  selectedStatus: 'PENDING' | 'ACCEPTED' | null = null;
  dateFrom: string | null = null;
  dateTo: string | null = null;

  // Deals data
  deals: CargoDeal[] = [];
  loadingDeals = false;
  errorDeals: string | null = null;
  dealsPage: PageResponse<CargoDeal> | null = null;
  currentDealsPage = 0;

  // Calculations data
  calculations: CustomsCalculation[] = [];
  loadingCalculations = false;
  errorCalculations: string | null = null;
  calculationsPage: PageResponse<CustomsCalculation> | null = null;
  currentCalculationsPage = 0;
  currentCalculationsFilters: SearchCustomsCalculationsRequest = {};

  // Supplies data (свои расчёты в статусе "В обработке")
  supplies: CustomsCalculation[] = [];
  loadingSupplies = false;
  errorSupplies: string | null = null;
  suppliesPage: PageResponse<CustomsCalculation> | null = null;
  currentSuppliesPage = 0;
  currentSuppliesFilters: SearchCustomsCalculationsRequest = {};

  /** По сделкам на странице: маршрут, цена, этап. */
  dealInfo: Record<number, DealInfo | null> = {};

  /** Фильтр запросов по статусу — чипами. */
  readonly statusFilters: { value: 'PENDING' | 'ACCEPTED' | null; key: string; fallback: string }[] = [
    { value: null, key: 'coDash.all', fallback: 'Все' },
    { value: 'PENDING', key: 'coDash.pending', fallback: 'Принимают ставки' },
    { value: 'ACCEPTED', key: 'coDash.accepted', fallback: 'Экспедитор выбран' },
  ];

  // Common data
  countries: CountryDto[] = [];

  // Translations
  titleText = 'Кабинет грузовладельца';
  createRequestText = 'Создать запрос';
  requestsTabText = 'Запросы';
  dealsTabText = 'Сделки';
  requestNumberText = 'Номер запроса';
  routeText = 'Маршрут';
  totalWeightText = 'Общий вес';
  totalVolumeText = 'Общий объем';
  bidsCountText = 'Количество ставок';
  awaitingBidText = 'Ожидает ставку';
  noRequestsText = 'Нет запросов';
  noDealsText = 'Нет сделок';
  loadingText = 'Загрузка...';
  dealNumberText = 'Номер сделки';
  freightForwarderText = 'Экспедитор';
  statusText = 'Статус';
  createdAtText = 'Дата создания';
  acceptedText = 'Принят';
  draftText = 'Черновик';
  pendingText = 'Ожидает';
  allStatusesText = 'Все статусы';
  dateFromText = 'Дата создания от';
  dateToText = 'Дата создания до';
  clearFiltersText = 'Очистить фильтры';
  calculationsTabText = 'Расчеты';
  suppliesTabText = 'Мои поставки';
  suppliesTitleText = 'Мои поставки';
  suppliesSubtitleText = 'Расчёты, переданные на сопровождение таможенному брокеру';
  suppliesEmptyText = 'У вас пока нет поставок в обработке';

  private subscriptions = new Subscription();

  constructor(
    private cargoRequestService: CargoRequestService,
    private dealService: DealService,
    private dictionaryService: DictionaryService,
    private customsCalculationsService: CustomsCalculationsService,
    private languageService: LanguageService,
    private authService: AuthService,
    private router: Router,
    private route: ActivatedRoute,
    public companyContext: CompanyContextService,
    private cdr: ChangeDetectorRef
  ) {}

  ngOnInit(): void {
    this.companyContext.ensureLoaded();
    // ВАЖНО: Используем setTimeout для отложенной инициализации на клиенте
    // Это гарантирует, что код выполнится только на клиенте после гидратации
    if (typeof window === 'undefined' || !window.localStorage) {
      // Если мы на сервере (SSR), просто выходим - компонент загрузится на клиенте
      console.warn('Dashboard: Running on server (SSR), skipping initialization');
      return;
    }

    // Используем setTimeout(0) чтобы код выполнился после полной инициализации Angular на клиенте
    setTimeout(() => {
      // Проверяем аутентификацию
      const token = this.authService.getToken();
      console.log('Dashboard ngOnInit: Checking authentication', {
        hasToken: !!token,
        tokenPreview: token ? token.substring(0, 30) + '...' : 'NO TOKEN',
        localStorageAvailable: typeof window !== 'undefined' && !!window.localStorage
      });

      if (!this.authService.isAuthenticated()) {
        console.warn('Dashboard: User not authenticated, redirecting to login');
        this.router.navigate(['/login']);
        return;
      }

      // Читаем активную вкладку из URL
      this.subscriptions.add(
        this.route.paramMap.subscribe(params => {
          const tab = params.get('tab') as 'requests' | 'deals' | 'calculations' | 'supplies';
          if (tab && ['requests', 'deals', 'calculations', 'supplies'].includes(tab)) {
            this.activeTab = tab;
            this.loadTabData(tab);
          }
          this.cdr.markForCheck();
        })
      );

      this.loadCountries();
      this.updateTranslations();
      
      this.subscriptions.add(
        this.languageService.getTranslations().subscribe(() => {
          this.updateTranslations();
        })
      );
    }, 0);
  }

  private loadTabData(tab: 'requests' | 'deals' | 'calculations' | 'supplies'): void {
    if (tab === 'requests' && this.requests.length === 0 && !this.loadingRequests) {
      this.loadRequests();
    } else if (tab === 'deals' && this.deals.length === 0 && !this.loadingDeals) {
      this.loadDeals();
    } else if (tab === 'calculations' && this.calculations.length === 0 && !this.loadingCalculations) {
      this.loadCalculations();
    } else if (tab === 'supplies' && this.supplies.length === 0 && !this.loadingSupplies) {
      this.loadSupplies();
    }
  }

  switchTab(tab: CoTab): void {
    // Навигация на новый URL - компонент перезагрузит данные через paramMap subscription
    this.router.navigate(['/cargo-owner/dashboard', tab]);
  }

  ngOnDestroy(): void {
    this.subscriptions.unsubscribe();
  }

  private updateTranslations(): void {
    this.titleText = this.languageService.translate('cargoOwner.dashboard.title') || 'Кабинет грузовладельца';
    this.createRequestText = this.languageService.translate('cargoOwner.dashboard.createRequest') || 'Создать запрос';
    this.requestsTabText = this.languageService.translate('cargoOwner.dashboard.requestsTab') || 'Запросы';
    this.dealsTabText = this.languageService.translate('cargoOwner.dashboard.dealsTab') || 'Сделки';
    this.requestNumberText = this.languageService.translate('cargoOwner.dashboard.requestNumber') || 'Номер запроса';
    this.routeText = this.languageService.translate('cargoOwner.dashboard.route') || 'Маршрут';
    this.totalWeightText = this.languageService.translate('cargoOwner.dashboard.totalWeight') || 'Общий вес';
    this.totalVolumeText = this.languageService.translate('cargoOwner.dashboard.totalVolume') || 'Общий объем';
    this.bidsCountText = this.languageService.translate('cargoOwner.dashboard.bidsCount') || 'Количество ставок';
    this.awaitingBidText = this.languageService.translate('cargoOwner.dashboard.awaitingBid') || 'Ожидает ставку';
    this.noRequestsText = this.languageService.translate('cargoOwner.dashboard.noRequests') || 'Нет запросов';
    this.noDealsText = this.languageService.translate('cargoOwner.dashboard.noDeals') || 'Нет сделок';
    this.loadingText = this.languageService.translate('cargoOwner.dashboard.loading') || 'Загрузка...';
    this.dealNumberText = this.languageService.translate('cargoOwner.dashboard.dealNumber') || 'Номер сделки';
    this.freightForwarderText = this.languageService.translate('cargoOwner.dashboard.freightForwarder') || 'Экспедитор';
    this.statusText = this.languageService.translate('cargoOwner.dashboard.status') || 'Статус';
    this.createdAtText = this.languageService.translate('cargoOwner.dashboard.createdAt') || 'Дата создания';
    this.acceptedText = this.languageService.translate('cargoOwner.dashboard.accepted') || 'Принят';
    this.draftText = this.languageService.translate('cargoOwner.dashboard.draft') || 'Черновик';
    this.pendingText = this.languageService.translate('cargoOwner.dashboard.pending') || 'Ожидает';
    this.allStatusesText = this.languageService.translate('cargoOwner.dashboard.allStatuses') || 'Все статусы';
    this.dateFromText = this.languageService.translate('cargoOwner.dashboard.dateFrom') || 'Дата создания от';
    this.dateToText = this.languageService.translate('cargoOwner.dashboard.dateTo') || 'Дата создания до';
    this.clearFiltersText = this.languageService.translate('cargoOwner.dashboard.clearFilters') || 'Очистить фильтры';
    this.calculationsTabText = this.languageService.translate('cargoOwner.dashboard.calculationsTab') || 'Расчеты';
    this.suppliesTabText = this.languageService.translate('cargoOwner.dashboard.suppliesTab') || 'Мои поставки';
    this.suppliesTitleText = this.languageService.translate('cargoOwner.dashboard.suppliesTitle') || this.suppliesTitleText;
    this.suppliesSubtitleText = this.languageService.translate('cargoOwner.dashboard.suppliesSubtitle') || this.suppliesSubtitleText;
    this.suppliesEmptyText = this.languageService.translate('cargoOwner.dashboard.suppliesEmpty') || this.suppliesEmptyText;
    this.cdr.markForCheck();
  }

  private loadCountries(): void {
    this.subscriptions.add(
      this.dictionaryService.getAllCountries().subscribe({
        next: (countries) => {
          this.countries = countries;
          this.cdr.markForCheck();
        },
        error: () => {
          // Игнорируем ошибки загрузки стран
          this.cdr.markForCheck();
        }
      })
    );
  }

  loadRequests(): void {
    this.loadRequestsPage(this.currentRequestsPage);
  }

  loadRequestsPage(page: number): void {
    this.loadingRequests = true;
    this.errorRequests = null;
    this.currentRequestsPage = page;

    // Формируем фильтр
    const filter: SearchCargoRequestsRequest = {};
    
    if (this.selectedStatus) {
      filter.status = this.selectedStatus;
    }
    
    if (this.dateFrom) {
      filter.dateFrom = this.dateFrom;
    }
    
    if (this.dateTo) {
      filter.dateTo = this.dateTo;
    }

    if (this.requestManagerFilter.id !== null) {
      filter.assigneeId = this.requestManagerFilter.id;
    } else if (this.requestManagerFilter.unassigned) {
      filter.assigneeUnassigned = true;
    }

    this.subscriptions.add(
      this.cargoRequestService.searchCargoRequests(filter, page, this.pageSize).subscribe({
        next: (pageResponse) => {
          console.log('CargoOwnerDashboard: Requests loaded successfully', pageResponse);
          this.requestsPage = pageResponse;
          this.requests = pageResponse.content;
          this.loadingRequests = false;
          this.cdr.markForCheck();
        },
        error: (err) => {
          console.error('CargoOwnerDashboard: Error loading cargo requests', err);
          
          if (err.status === 401) {
            this.errorRequests = 'Сессия истекла. Пожалуйста, войдите снова.';
          } else if (err.status === 403) {
            this.errorRequests = this.languageService.translate('cargoOwner.dashboard.error') || 'Ошибка доступа (403).';
          } else {
            this.errorRequests = this.languageService.translate('cargoOwner.dashboard.error') || 'Ошибка при загрузке запросов';
          }
          this.loadingRequests = false;
          this.cdr.markForCheck();
        }
      })
    );
  }

  onRequestsPageChange(page: number): void {
    this.loadRequestsPage(page);
    window.scrollTo({ top: 0, behavior: 'smooth' });
  }

  // ── Ответственные сотрудники (распределение внутри компании) ──
  requestManagerFilter: AssigneeFilterValue = EMPTY_ASSIGNEE_FILTER;
  dealManagerFilter: AssigneeFilterValue = EMPTY_ASSIGNEE_FILTER;
  dealOperatorFilter: AssigneeFilterValue = EMPTY_ASSIGNEE_FILTER;

  /** Администратор компании: видит менеджеров запросов и фильтрует по ним. */
  get showManagerColumn(): boolean {
    return this.companyContext.hasPermission('ASSIGN_REQUESTS');
  }

  /** Администратор и менеджер: видят операторов сделок и фильтруют по ним. */
  get showOperatorColumn(): boolean {
    return this.companyContext.hasPermission('ASSIGN_DEALS');
  }

  onRequestManagerFilter(value: AssigneeFilterValue): void {
    this.requestManagerFilter = value;
    this.applyFilters();
  }

  onDealFilterChange(): void {
    this.loadDealsPage(0);
  }

  private dealFilterParams(): DealAssigneeFilter {
    return {
      managerId: this.dealManagerFilter.id,
      operatorId: this.dealOperatorFilter.id,
      operatorUnassigned: this.dealOperatorFilter.unassigned
    };
  }

  loadDeals(): void {
    this.loadDealsPage(this.currentDealsPage);
  }

  loadDealsPage(page: number): void {
    this.loadingDeals = true;
    this.errorDeals = null;
    this.currentDealsPage = page;

    this.subscriptions.add(
      this.dealService.getMyDealsPaginated(page, this.pageSize, this.dealFilterParams()).subscribe({
        next: (pageResponse) => {
          console.log('CargoOwnerDashboard: Deals loaded successfully', pageResponse);
          this.dealsPage = pageResponse;
          this.deals = pageResponse.content;
          this.loadingDeals = false;
          this.loadDealInfo(this.deals.map(d => d.id));
          this.cdr.markForCheck();
        },
        error: (err) => {
          console.error('CargoOwnerDashboard: Error loading deals', err);
          
          if (err.status === 401) {
            this.errorDeals = 'Сессия истекла. Пожалуйста, войдите снова.';
          } else if (err.status === 403) {
            this.errorDeals = 'Ошибка доступа (403).';
          } else {
            this.errorDeals = 'Ошибка при загрузке сделок';
          }
          this.loadingDeals = false;
          this.cdr.markForCheck();
        }
      })
    );
  }

  onDealsPageChange(page: number): void {
    this.loadDealsPage(page);
    window.scrollTo({ top: 0, behavior: 'smooth' });
  }

  /** Маршрут, цена и последний этап по каждой сделке на странице (по одной карточке на сделку). */
  private loadDealInfo(ids: number[]): void {
    const todo = ids.filter(id => !(id in this.dealInfo));
    if (!todo.length) return;
    const lang = this.languageService.getCurrentLanguageValue();
    const name = (c?: { nameRu: string; nameEn: string }) => (c ? (lang === 'en' || lang === 'zh' ? c.nameEn : c.nameRu) : '');
    this.subscriptions.add(
      forkJoin(todo.map(id => this.dealService.getDealDetail(id).pipe(catchError(() => of(null))))).subscribe(details => {
        details.forEach((d, i) => {
          if (!d) { this.dealInfo[todo[i]] = null; return; }
          const r = d.cargoRequest;
          const last = (d.activities ?? [])
            .filter(a => a.activityType?.category === 'STATUS')
            .sort((a, b) => new Date(b.createdAt).getTime() - new Date(a.createdAt).getTime())[0];
          const names = (r?.products ?? []).map(p => p.name).filter((n): n is string => !!n);
          this.dealInfo[todo[i]] = {
            route: `${r?.fromCity ?? ''} → ${r?.toCity ?? ''}`,
            cargo: names.length > 1 ? `${names[0]} ${this.t('coDash.andMore', 'и ещё')} ${names.length - 1}` : (names[0] ?? ''),
            countries: [name(r?.fromCountry), name(r?.toCountry)].filter(Boolean).join(' → '),
            requestCreatedAt: r?.createdAt || d.createdAt,
            price: d.bid ? this.money(d.bid.price, d.bid.currencyCode) : '—',
            stage: last ? ((lang === 'en' ? last.activityType.nameEn : last.activityType.nameRu) || '—') : this.t('coDash.noStage', 'Ждём первый статус'),
            stageDate: last?.createdAt ?? null,
          };
        });
        this.cdr.markForCheck();
      })
    );
  }

  get tabTitle(): { key: string; fallback: string } {
    return TAB_TITLES[this.activeTab];
  }

  t(key: string, fallback: string): string {
    const value = this.languageService.translate(key);
    return value && value !== key ? value : fallback;
  }

  countryName(id: number | undefined): string {
    const c = this.countries.find(x => x.id === id);
    if (!c) return '';
    const lang = this.languageService.getCurrentLanguageValue();
    return lang === 'en' || lang === 'zh' ? (c.nameEn || c.nameRu) : (c.nameRu || c.nameEn);
  }

  money(amount: number, code: string): string {
    const lang = this.languageService.getCurrentLanguageValue();
    const locale = ({ ru: 'ru-RU', kk: 'kk-KZ', en: 'en-US', zh: 'zh-CN' } as Record<string, string>)[lang] ?? 'ru-RU';
    return `${new Intl.NumberFormat(locale, { maximumFractionDigits: 2 }).format(amount || 0)} ${code || ''}`.trim();
  }

  setStatusFilter(value: 'PENDING' | 'ACCEPTED' | null): void {
    if (this.selectedStatus === value) return;
    this.selectedStatus = value;
    this.applyFilters();
  }

  /** «1 ставка», «3 ставки», «5 ставок». */
  bidsLabel(n: number): string {
    const lang = this.languageService.getCurrentLanguageValue();
    if (lang !== 'ru') return `${n} ${this.t('coDash.bidsShort', 'bids')}`;
    const mod10 = n % 10, mod100 = n % 100;
    const word = mod10 === 1 && mod100 !== 11 ? 'ставка' : mod10 >= 2 && mod10 <= 4 && (mod100 < 12 || mod100 > 14) ? 'ставки' : 'ставок';
    return `${n} ${word}`;
  }

  requestStatusText(request: CargoRequest): string {
    if (request.isDraft) return this.draftText;
    if (request.hasDeal) return this.t('coDash.accepted', 'Экспедитор выбран');
    return this.t('coDash.pending', 'Принимает ставки');
  }

  requestTone(request: CargoRequest): string {
    if (request.isDraft) return 'gray';
    return request.hasDeal ? 'green' : 'open';
  }

  getTotalWeight(request: CargoRequest): number {
    return request.products.reduce((sum, product) => sum + product.totalWeightKg, 0);
  }

  getTotalVolume(request: CargoRequest): number {
    return request.products.reduce((sum, product) => {
      // Если есть volumeM3, используем его
      if (product.volumeM3) {
        return sum + product.volumeM3;
      }
      // Иначе вычисляем из габаритов одного места × количество мест
      if (product.lengthMm && product.widthMm && product.heightMm) {
        const volume = (product.lengthMm * product.widthMm * product.heightMm) / 1_000_000_000 * (product.quantity || 1);
        return sum + volume;
      }
      // Если ни габариты, ни объем не заполнены, возвращаем 0
      return sum;
    }, 0);
  }

  /** Вес всегда в кг (как у экспедитора), объём — до сотых. */
  formatWeight(weight: number): string {
    return `${this.num(weight, 1)} ${this.t('ffSearch.kg', 'кг')}`;
  }

  formatVolume(volume: number): string {
    return `${this.num(volume, volume > 0 && volume < 0.01 ? 3 : 2)} м³`;
  }

  private num(n: number, digits: number): string {
    const lang = this.languageService.getCurrentLanguageValue();
    const locale = ({ ru: 'ru-RU', kk: 'kk-KZ', en: 'en-US', zh: 'zh-CN' } as Record<string, string>)[lang] ?? 'ru-RU';
    return new Intl.NumberFormat(locale, { maximumFractionDigits: digits }).format(n || 0);
  }

  viewRequest(id: number): void {
    this.router.navigate(['/cargo-owner/request', id]);
  }

  viewDeal(dealId: number): void {
    this.router.navigate(['/cargo-owner/deal', dealId]);
  }


  hasActiveFilters(): boolean {
    return this.selectedStatus !== null || this.dateFrom !== null || this.dateTo !== null
      || this.requestManagerFilter.id !== null || this.requestManagerFilter.unassigned;
  }

  applyFilters(): void {
    this.currentRequestsPage = 0;
    this.loadRequestsPage(0);
  }

  clearFilters(): void {
    this.selectedStatus = null;
    this.dateFrom = null;
    this.dateTo = null;
    this.requestManagerFilter = EMPTY_ASSIGNEE_FILTER;
    this.applyFilters();
  }

  formatDate(dateString?: string): string {
    if (!dateString) return '-';
    try {
      const date = new Date(dateString);
      return date.toLocaleDateString('ru-RU', {
        year: 'numeric',
        month: '2-digit',
        day: '2-digit'
      });
    } catch {
      return dateString;
    }
  }

  // Calculations methods
  loadCalculations(): void {
    this.loadCalculationsPage(this.currentCalculationsPage, this.currentCalculationsFilters);
  }

  loadCalculationsPage(page: number, filters: SearchCustomsCalculationsRequest): void {
    this.loadingCalculations = true;
    this.errorCalculations = null;
    this.currentCalculationsPage = page;
    this.currentCalculationsFilters = filters;

    this.subscriptions.add(
      this.customsCalculationsService.searchCalculations(filters, page, this.pageSize).subscribe({
        next: (pageResponse) => {
          this.calculationsPage = pageResponse;
          this.calculations = pageResponse.content;
          this.loadingCalculations = false;
          this.cdr.markForCheck();
        },
        error: (err) => {
          console.error('Error loading calculations:', err);
          this.errorCalculations = 'Ошибка при загрузке расчетов';
          this.loadingCalculations = false;
          this.cdr.markForCheck();
        }
      })
    );
  }

  onCalculationsPageChange(page: number): void {
    this.loadCalculationsPage(page, this.currentCalculationsFilters);
    window.scrollTo({ top: 0, behavior: 'smooth' });
  }

  onCalculationsFilterChange(filters: SearchCustomsCalculationsRequest): void {
    this.currentCalculationsPage = 0;
    this.loadCalculationsPage(0, filters);
  }

  onCreateCalculation(): void {
    this.router.navigate(['/calculations/new']);
  }

  onViewCalculation(id: number): void {
    // Если расчёт уже не в статусе "Создан" — сразу открываем страницу обработки
    // (две вкладки: расчёт + перевозка), а не промежуточный простой просмотр.
    const calculation = this.calculations.find(c => c.id === id);
    if (calculation?.status && calculation.status !== 'CREATED') {
      this.router.navigate(['/calculations', id, 'process']);
      return;
    }
    this.router.navigate(['/calculations', id]);
  }

  async onDeleteCalculation(id: number): Promise<void> {
    if (await this.confirmDialog.confirm(this.languageService.translate('common.deleteConfirm') || 'Вы уверены, что хотите удалить этот расчет?')) {
      this.subscriptions.add(
        this.customsCalculationsService.deleteCalculation(id).subscribe({
          next: () => {
            this.loadCalculations();
          },
          error: (err) => {
            console.error('Error deleting calculation:', err);
            this.errorCalculations = this.languageService.translate('common.deleteError') || 'Ошибка при удалении расчета';
            this.cdr.markForCheck();
          }
        })
      );
    }
  }

  // Supplies methods
  loadSupplies(): void {
    this.loadSuppliesPage(this.currentSuppliesPage, this.currentSuppliesFilters);
  }

  /** Сделки без расчёта — тоже поставки (их запрос создавали не из расчёта). Показываем на первой странице списка. */
  dealSupplies: CargoDeal[] = [];

  private loadDealSupplies(): void {
    const calcs$ = this.customsCalculationsService.searchCalculations({}, 0, 500).pipe(catchError(() => of(null)));
    const deals$ = this.dealService.getMyDealsPaginated(0, 200).pipe(catchError(() => of(null)));
    this.subscriptions.add(
      forkJoin([calcs$, deals$]).subscribe(([calcs, deals]) => {
        // Запросы, созданные из расчётов, — их сделки уже в поставке по расчёту
        const linked = new Set<number>();
        (calcs?.content ?? []).forEach(c => (c.cargoRequests ?? []).forEach(r => linked.add(r.id)));
        this.dealSupplies = calcs ? (deals?.content ?? []).filter(d => !linked.has(d.cargoRequestId)) : [];
        this.loadDealInfo(this.dealSupplies.map(d => d.id));
        this.cdr.markForCheck();
      })
    );
  }

  /** Сделки-поставки под фильтр: поиск — по маршруту, товарам и экспедитору, период — по дате сделки. */
  get filteredDealSupplies(): CargoDeal[] {
    if ((this.suppliesPage?.number ?? 0) > 0) return [];
    const q = this.suppliesSearch.trim().toLowerCase();
    const from = this.suppliesDateFrom ? new Date(this.suppliesDateFrom).getTime() : null;
    const to = this.suppliesDateTo ? new Date(this.suppliesDateTo).getTime() + 86400000 : null;
    return this.dealSupplies.filter(d => {
      const at = new Date(d.createdAt).getTime();
      if (from != null && at < from) return false;
      if (to != null && at >= to) return false;
      if (!q) return true;
      const info = this.dealInfo[d.id];
      return [info?.route, info?.cargo, info?.countries, d.freightForwarderCompanyName].some(v => (v || '').toLowerCase().includes(q));
    });
  }

  openDealSupply(id: number): void {
    this.router.navigate(['/cargo-owner/supplies/deal', id]);
  }

  loadSuppliesPage(page: number, filters: SearchCustomsCalculationsRequest): void {
    if (!this.dealSupplies.length && page === 0) {
      this.loadDealSupplies();
    }
    this.loadingSupplies = true;
    this.errorSupplies = null;
    this.currentSuppliesPage = page;
    this.currentSuppliesFilters = filters;

    this.subscriptions.add(
      this.customsCalculationsService
        .searchCalculations({ ...filters, status: 'IN_PROCESSING' }, page, this.pageSize)
        .subscribe({
          next: (pageResponse) => {
            this.suppliesPage = pageResponse;
            this.supplies = pageResponse.content;
            this.loadingSupplies = false;
            this.cdr.markForCheck();
          },
          error: (err) => {
            console.error('Error loading supplies:', err);
            this.errorSupplies = 'Ошибка при загрузке поставок';
            this.loadingSupplies = false;
            this.cdr.markForCheck();
          }
        })
    );
  }

  onSuppliesPageChange(page: number): void {
    this.loadSuppliesPage(page, this.currentSuppliesFilters);
    window.scrollTo({ top: 0, behavior: 'smooth' });
  }

  onSuppliesFilterChange(filters: SearchCustomsCalculationsRequest): void {
    this.currentSuppliesPage = 0;
    this.loadSuppliesPage(0, filters);
  }

  // ─── Фильтр поставок: поиск и период ───
  suppliesSearch = '';
  suppliesDateFrom: string | null = null;
  suppliesDateTo: string | null = null;

  applySuppliesFilters(): void {
    this.onSuppliesFilterChange({
      title: this.suppliesSearch.trim() || undefined,
      dateFrom: this.suppliesDateFrom || undefined,
      dateTo: this.suppliesDateTo || undefined
    });
  }

  hasSuppliesFilters(): boolean {
    return !!(this.suppliesSearch.trim() || this.suppliesDateFrom || this.suppliesDateTo);
  }

  clearSuppliesFilters(): void {
    this.suppliesSearch = '';
    this.suppliesDateFrom = null;
    this.suppliesDateTo = null;
    this.applySuppliesFilters();
  }

  private supplyProductNames(c: CustomsCalculation): string[] {
    return (c.products || []).map(p => p.nameForBroker || p.nameOriginal).filter((n): n is string => !!n);
  }

  /** «Товар и ещё 2»; без товаров — название расчёта. */
  supplyProducts(c: CustomsCalculation): string {
    const names = this.supplyProductNames(c);
    if (!names.length) return c.title || '—';
    return names.length === 1 ? names[0] : `${names[0]} ${this.t('coDash.andMore', 'и ещё')} ${names.length - 1}`;
  }

  supplyProductsFull(c: CustomsCalculation): string {
    return this.supplyProductNames(c).join('\n') || c.title || '';
  }

  /** Сумма в тенге, как в таблицах кабинета: «801 678 ₸». */
  kztMoney(v?: number | null): string {
    if (v == null) return '—';
    return new Intl.NumberFormat('ru-RU', { maximumFractionDigits: 0 }).format(Math.round(v)) + ' ₸';
  }

  onOpenSupply(id: number): void {
    this.router.navigate(['/cargo-owner/supplies', id]);
  }
}
